import { createContext, useCallback, useContext, useEffect, useState } from "react";

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "system",
  resolved: "light",
  setTheme: () => {},
});

const STORAGE_KEY = "gompassql:theme";

function systemPrefers(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "dark";
    const saved = window.localStorage.getItem(STORAGE_KEY) as Theme | null;
    const t = saved ?? "dark";
    // Toggle .dark synchronously during state init — before the first
    // paint — so CSS variables (--background etc.) resolve correctly
    // from the very first frame. The useEffect below handles subsequent
    // changes and system-preference listeners.
    const applied: ResolvedTheme = t === "system" ? systemPrefers() : t;
    document.documentElement.classList.toggle("dark", applied === "dark");
    return t;
  });
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    theme === "system" ? systemPrefers() : theme,
  );

  useEffect(() => {
    const applied: ResolvedTheme = theme === "system" ? systemPrefers() : theme;
    setResolved(applied);
    document.documentElement.classList.toggle("dark", applied === "dark");
    window.localStorage.setItem(STORAGE_KEY, theme);

    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      const next: ResolvedTheme = e.matches ? "dark" : "light";
      setResolved(next);
      document.documentElement.classList.toggle("dark", next === "dark");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
